"use client";

import { AnimatePresence, motion } from "framer-motion";
import React, { useState } from "react";

import styles from "./ContactUs.module.css";
import { useForm } from "react-hook-form";
import { useTranslations } from "next-intl";

type FormData = {
  name: string;
  title: string;
  message: string;
  email: string;
};

// Helper function to encode form data for Netlify
const encode = (dataToEncode: { [key: string]: any }): string => {
  return Object.keys(dataToEncode)
    .map(
      (key) =>
        encodeURIComponent(key) + "=" + encodeURIComponent(dataToEncode[key])
    )
    .join("&");
};

const ContactUs = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null); // Added state for submission errors
  const t = useTranslations("contactForm");

  const onSubmit = async (data: FormData, event?: React.BaseSyntheticEvent) => {
    setLoading(true);
    setSubmitError(null); // Reset error on new submission

    const form = event?.target as HTMLFormElement;
    // Use form.action; default to current path if not available. Netlify often expects POST to the page path.
    const action = form?.action || window.location.pathname; 

    try {
      const response = await fetch(action, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({ "form-name": "contact", ...data }), // "contact" is the name of your form in HTML
      });

      if (response.ok) {
        setSubmitted(true);
        reset(); // Clear form fields on successful submission
        setTimeout(() => setSubmitted(false), 3000); // Reset submitted state after 3 seconds
      } else {
        // Handle HTTP errors from Netlify or server
        setSubmitError(t("errorMessage"));
        console.error("Form submission failed:", response);
      }
    } catch (error) {
      // Handle network errors or other exceptions
      setSubmitError(t("errorMessage"));
      console.error("Form submission error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.form}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        method="POST" // Good for non-JS fallback and Netlify detection
        data-netlify="true"
        name="contact" // This name is used in the "form-name" hidden field
        action="/?no-cache=1" // Action for non-JS / Netlify (can be current page)
      >
        {/* Hidden field for Netlify to identify the form */}
        <input type="hidden" name="form-name" value="contact" />

        <div className={styles.row}>
          <label htmlFor="name">{t("name")}</label>
          <input
            type="text"
            placeholder={t("namePlaceholder")}
            {...register("name", { required: t("nameRequired") })}
          />
          {errors.name && (
            <span className={styles.error}>{errors.name.message}</span>
          )}
        </div>
        <div className={styles.row}>
          <label htmlFor="email">{t("email")}</label>
          <input
            type="email"
            placeholder={t("emailPlaceholder")}
            {...register("email", { required: t("emailRequired") })}
          />
          {errors.email && (
            <span className={styles.error}>{errors.email.message}</span>
          )}
        </div>
        <div className={styles.row}>
          <label htmlFor="title">{t("title")}</label>
          <input
            type="text"
            placeholder={t("titlePlaceholder")}
            {...register("title", { required: t("titleRequired") })}
          />
          {errors.title && (
            <span className={styles.error}>{errors.title.message}</span>
          )}
        </div>
        <div className={styles.row}>
          <label htmlFor="message">{t("message")}</label>
          <textarea
            rows={4}
            placeholder={t("messagePlaceholder")}
            {...register("message", { required: t("messageRequired") })}
          ></textarea>
          {errors.message && (
            <span className={styles.error}>{errors.message.message}</span>
          )}
        </div>

        <motion.button
          className={styles.submitButton}
          type="submit"
          disabled={loading || submitted}
          initial={false}
          animate={{
            backgroundColor: submitted ? "#4caf50" : submitError ? "#f44336" : "transparent", // Green on success, Red on error
            color: "#fff",
            border: "solid white 1px",
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        >
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.span
                key="loading"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                {t("loading")}
              </motion.span>
            ) : submitted ? (
              <motion.span
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                ✅
              </motion.span>
            ) : submitError ? ( // Display error indicator
              <motion.span
                key="error"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                ⚠️
              </motion.span>
            ) : (
              <motion.span
                key="default"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
              >
                {t("sendMessage")}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
        {/* Display error message below the button */}
        {submitError && (
          <motion.p
            key="submitErrorMessage"
            className={styles.error} // Ensure you have a style for this
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            style={{ marginTop: '10px', textAlign: 'center' }}
          >
            {submitError}
          </motion.p>
        )}
      </form>
    </div>
  );
};

export default ContactUs;
